import React, { useEffect, useState, useRef } from "react";
import { useFieldArray, Controller, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  TextField,
  IconButton,
  Button,
  Tooltip,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Box,
  Icon,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { green, grey } from "@mui/material/colors";
import _ from "lodash";
import { AuraSimpleTableHead } from "app/main/apps/common/AuraSimpleTable";
import dispatchMessage, {
  checkNullOrUndefined,
  formatter,
  isCustomer,
  isValidNumber,
  preventNegativeValues,
} from "app/main/apps/common/AuraFunctions";
import {
  getRecordsByCategory,
  getUniqueCategories,
  getUnitPriceRecord,
  getVasPriceListHeader,
  openVasPreview,
} from "./VasHelper";
import {
  PriceListHeaderTitleSection,
  PriceListHeaderArea,
} from "./VasStyledComponent";
import { getVasPriceList } from "../../store/orderSlice";
import { calculateVasLineTotalExact, getDiscountAmtExact } from "app/main/apps/common/money";

const highlightedInputStyle = {
  "& .MuiInput-underline:before": {
    borderBottomColor: "#4f46e5", // Default bottom border color
    borderBottomWidth: "2px",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#4f46e5", // Active (focused) bottom border color
  },
  "& .MuiFormLabel-root": {
    color: "#4f46e5", // Label color
  },
};

const highlightedBoxInputStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#4f46e5", // Default border color
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#4f46e5", // Hover border color
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4f46e5", // Focused border color
    },
  },
};

const VasPriceList = (props) => {
  const {
    type,
    resetData = true,
    vasData,
    completeOrderData,
    vasPriceDetailsData,
    totalVASAmount,
    setTotalVASAmount,
    from,
    hideAmountDetails = false,
  } = props;
  const dispatch = useDispatch();
  const vasPriceLists = useSelector(
    ({ eCommerceApp }) => eCommerceApp.order.vasPriceLists
  );
  const currentUser = useSelector(({ user }) => user);
  const isCustomerUser = isCustomer(currentUser?.role) ?? false;
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const methods = useFormContext();
  const didAppendRef = useRef(false);
  const {
    control,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = methods;
  const {
    fields: vasFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "vas_price_details",
    keyName: "key",
  });
  const vasPriceDetails = watch("vas_price_details");
  const categories = getUniqueCategories(vasPriceLists) || [];
  const services = !checkNullOrUndefined(selectedCategory)
    ? getRecordsByCategory(vasPriceLists, selectedCategory)
    : [];
  const isView = type === "view";
  const headerType = type === "view" && hideAmountDetails ? "base" : type;
  const vasHeader = getVasPriceListHeader(headerType, isCustomerUser);
  const isOrderPageStyle = from === "order";
  // const isDetailPageStyle = from === "detail";
  const isChooseSupplierPageStyle = from === "chooseSupplier";
  const printStyle = from === "print";
  const [vasServices, setVasServices] = useState(vasData?.services || {});

  useEffect(() => {
    if (checkNullOrUndefined(vasPriceLists) || vasPriceLists?.length === 0) {
      const vasPriceListParam = completeOrderData?.customer?.id
        ? { customer_id: completeOrderData?.customer?.id }
        : {};
      dispatch(getVasPriceList(vasPriceListParam));
    }
  }, []);

  /**
   * Initialize the data
   */
  useEffect(() => {
    if (!didAppendRef.current && vasPriceDetailsData?.length > 0 && resetData) {
      vasPriceDetailsData.forEach((item, index) => {
        const alreadyExists = _.some(vasPriceDetails, {
          detail_id: item.detail_id,
        });
        if (!alreadyExists) {
          append(item);
          trigger(`vas_price_details.${index}.total_price`);
        }
      });
      didAppendRef.current = true;
    }
  }, []);

  /**
   * This method handles VAS Quote Add function
   */
  const handleAdd = () => {
    if (selectedCategory && selectedService) {
      const alreadyExists = vasFields?.some(
        (item) =>
          item.name === selectedService && item.category === selectedCategory
      );
      const unitPriceRecord = getUnitPriceRecord(
        vasPriceLists,
        selectedCategory,
        selectedService
      );
      if (!alreadyExists && unitPriceRecord && unitPriceRecord.length > 0) {
        const unitPriceRecordTemp = _.cloneDeep(unitPriceRecord[0]);
        unitPriceRecordTemp.vas_price_list_id = unitPriceRecordTemp?.id;
        unitPriceRecordTemp.quantity = "";
        if (unitPriceRecordTemp?.id) delete unitPriceRecordTemp.id;
        append(unitPriceRecordTemp);
        const newItemIndex = vasPriceDetails.length;
        trigger(`vas_price_details.${newItemIndex}.total_price`);
        setSelectedCategory(null);
        setSelectedService(null);
      } else {
        dispatchMessage(dispatch, "Already exists", "error", 2000);
      }
    }
  };

  /**
   * This method handles to update total_price on unit_price, quantity, margin, discount change
   */
  const handleChange = (index) => {
    const {
      unit_price: unitPrice,
      quantity,
      margin,
      discount,
    } = vasPriceDetails[index];
    const validUnitPrice = isValidNumber(unitPrice) ? parseFloat(unitPrice) : 0;
    const validQuantity = isValidNumber(quantity) ? parseFloat(quantity) : 0;
    const validMargin = isValidNumber(margin) ? parseFloat(margin) : 0;
    const validDiscount = isValidNumber(discount) ? parseFloat(discount) : 0;
    let totalAmount;
    if (validUnitPrice !== 0 && validQuantity !== 0) {
      // PGSS-5025: same formula (base + base·margin% − base·discount%, rounded once), exact decimal
      // instead of float+toFixed. Keep the Number type and the undefined-when-empty contract.
      totalAmount = Number(
        calculateVasLineTotalExact(validUnitPrice, validQuantity, validMargin, validDiscount)
      );
    }
    setValue(`vas_price_details.${index}.total_price`, totalAmount, {
      shouldValidate: true,
    });
  };

  /**
   * Handles to check parent page
   */
  const isFromPage = (fromPage) => {
    return from === fromPage;
  };

  /**
   * Recalculate totalAmount whenever vasPriceDetails changes
   */
  useEffect(() => {
    // Simple watch("vas_price_details") doesn't reflect changes made with setValue(...) to nested vasFields
    // So Used watch with .subscribe() to respond to nested updates like total_price
    // But this will not handle the row addition and deletion
    const subscription = watch((value, { name }) => {
      if (
        name?.startsWith("vas_price_details") &&
        (name.endsWith(".total_price") ||
          name.match(/^vas_price_details\.\d+\./))
      ) {
        const details = value?.vas_price_details || [];
        const updatedTotal = details.reduce(
          (sum, item) => sum + (parseFloat(item?.total_price) || 0),
          0
        );
        if (setTotalVASAmount) setTotalVASAmount(updatedTotal);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  /**
   * Recalculate totalAmount whenever deletion or addition of vasFields changes
   */
  useEffect(() => {
    const details = watch("vas_price_details") || [];
    const updatedTotal = details?.reduce(
      (sum, item) => sum + (parseFloat(item?.total_price) || 0),
      0
    );
    if (setTotalVASAmount) setTotalVASAmount(updatedTotal);
  }, [vasFields.length]);

  /**
   * Returns diaply message when there is no record found
   */
  const getEmptyMessage = () => {
    if (isFromPage("chooseSupplier")) {
      return "No VAS quote found";
    }
    if (isView) {
      return "No VAS Price Details found";
    }
    return "Add atleast one VAS Price Details";
  };
  const handleVasPreview = () => {
    openVasPreview(dispatch, vasData, completeOrderData);
  };

  return (
    <div className={`flex mt-32 ${!isOrderPageStyle && "mb-48"}`}>
      <div className="flex flex-col w-full">
        {type === "add" && (
          <PriceListHeaderArea>
            <div className="w-full flex flex-row">
              <PriceListHeaderTitleSection className="w-full flex flex-row">
                {isFromPage("chooseSupplier")
                  ? "VAS Quote"
                  : "VAS Price Details"}
                {!checkNullOrUndefined(completeOrderData) &&
                  !checkNullOrUndefined(vasData) && (
                    <div className="ml-36">
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="success"
                            className="!py-0 !cursor-text"
                            checked={vasServices.sandBlasting || false}
                            disabled
                            name="Sand Blasting"
                            sx={{
                              "&.Mui-disabled": { color: "#2e7d32" }, // Change disabled checkbox color
                            }}
                          />
                        }
                        label="Sand Blasting"
                        sx={{
                          "&.Mui-disabled .MuiFormControlLabel-label": {
                            color: "#111 !important",
                          }, // Change label color when disabled
                        }}
                        className="!cursor-text"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="success"
                            className="!py-0 !cursor-text"
                            checked={vasServices.etching || false}
                            disabled
                            name="etching"
                            sx={{
                              "&.Mui-disabled": { color: "#2e7d32" }, // Change disabled checkbox color
                            }}
                          />
                        }
                        label="Etching"
                        sx={{
                          "&.Mui-disabled .MuiFormControlLabel-label": {
                            color: "#111 !important",
                          }, // Change label color when disabled
                        }}
                        className="!cursor-text"
                      />
                      <Button
                        size="small"
                        color="secondary"
                        variant="link"
                        className="text-green-700 rounded-md !py-0"
                        onClick={handleVasPreview}
                      >
                        Click here to view Sandblasting/Etching
                      </Button>
                    </div>
                  )}
              </PriceListHeaderTitleSection>
            </div>
            <div className="flex flex-col md:flex-row p-8 md:items-center">
              <div className="flex flex-col md:flex-row md:items-center">
                <Autocomplete
                  size="small"
                  className="md:mr-8 my-8 md:my-0 md:w-[350px]"
                  options={
                    !checkNullOrUndefined(categories) && categories?.length > 0
                      ? categories
                      : []
                  }
                  getOptionLabel={(option) => option}
                  value={selectedCategory}
                  onChange={(event, newValue) => {
                    setSelectedCategory(newValue);
                    setSelectedService(null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Category"
                      variant="outlined"
                    />
                  )}
                  sx={highlightedBoxInputStyle}
                />
                <Autocomplete
                  size="small"
                  className="md:mr-8 my-8 md:my-0 md:w-[350px]"
                  options={(!checkNullOrUndefined(services) &&
                  services?.length > 0
                    ? services
                    : []
                  ).map((service) => service?.name)}
                  getOptionLabel={(option) => option}
                  value={selectedService}
                  onChange={(event, newValue) => setSelectedService(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Service"
                      variant="outlined"
                    />
                  )}
                  disabled={!selectedCategory}
                  sx={highlightedBoxInputStyle}
                />
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  className="rounded-md !py-8 h-[3.4rem] my-8 md:my-0"
                  onClick={handleAdd}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </div>
              <div className="ml-auto flex flex-row items-center">
                <PriceListHeaderTitleSection>
                  Total VAS Amount:
                </PriceListHeaderTitleSection>
                {formatter.format(totalVASAmount)}
              </div>
            </div>
          </PriceListHeaderArea>
        )}
        {type === "view" && (
          <PriceListHeaderArea>
            <div
              className={`flex flex-col w-full md:flex-row md:items-center ${
                isChooseSupplierPageStyle ? "p-8" : "pr-8"
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center">
                <PriceListHeaderTitleSection>
                  VAS Price Details
                </PriceListHeaderTitleSection>
              </div>
            </div>
          </PriceListHeaderArea>
        )}
        <div className="my-16">
          {vasFields?.length > 0 && (
            <div className="table-responsive w-full">
              <Table
                className="simple w-full min-w-full"
                sx={{
                  "&.simple tbody tr td:first-of-type": {
                    padding: isOrderPageStyle ? "5px 8px" : "8px",
                  },
                  "&.simple thead tr th:first-of-type": {
                    padding: isOrderPageStyle ? "5px 8px" : "8px",
                  },
                  "&.simple thead tr th": {
                    padding: isOrderPageStyle ? "5px 8px" : "8px",
                    backgroundColor: isChooseSupplierPageStyle
                      ? "#f0f3fb"
                      : "#FFFFFF",
                    borderColor: isOrderPageStyle ? "#FFF" : "#EEE",
                    fontWeight: printStyle ? "400" : "600",
                    fontSize: printStyle ? "1.2rem !important" : "",
                  },
                  "&.simple tbody tr td": {
                    borderColor: isOrderPageStyle ? "#FFF" : "#EEE",
                    padding: isOrderPageStyle ? "8px" : "12px 8px",
                    fontSize: printStyle ? "1.2rem !important" : "",
                  },
                }}
              >
                <AuraSimpleTableHead
                  headerList={vasHeader}
                  classes={{
                    cnCell: "font-semibold px-8 py-8 capitalize",
                  }}
                />
                <TableBody>
                  {vasFields?.map((item, index) => {
                    const totalPrice = watch(
                      `vas_price_details.${index}.total_price`
                    );
                    return (
                      <TableRow key={item.key}>
                        <TableCell
                          className={`${
                            printStyle ? "text-sm" : ""
                          } "w-[14rem]"`}
                        >
                          {item.vas_type}
                        </TableCell>
                        <TableCell
                          className={`${
                            printStyle ? "text-sm" : ""
                          } "w-[25rem]"`}
                        >
                          {item.category || item.vas_price_list.category}
                        </TableCell>
                        <TableCell className="w-[25rem]">
                          {item.name || item.vas_price_list.name}
                        </TableCell>
                        <TableCell className="w-120 capitalize">
                          {item.unit_type || item.vas_price_list.unit_type}
                        </TableCell>
                        {isView && isCustomerUser ? (
                          <>
                            {/* CUSTOMER VIEW MODE                    */}
                            {/* Quantity FIRST, Unit Price SECOND     */}
                            <TableCell className="w-120">
                              {/* Quantity */}
                              {item.quantity}
                            </TableCell>

                            <TableCell className="w-120">
                              {/* Unit Price */}
                              {formatter.format(item.unit_price)}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            {/* NON-CUSTOMER VIEW   */}
                            {/* Unit Price FIRST, Quantity SECOND     */}
                            <TableCell className="w-120">
                              {!isView ? (
                                <Controller
                                  size="small"
                                  name={`vas_price_details.${index}.unit_price`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      onChange={(e) => {
                                        field.onChange(e);
                                        handleChange(index);
                                      }}
                                      label=""
                                      type="number"
                                      size="small"
                                      variant="standard"
                                      sx={highlightedInputStyle}
                                      onKeyDown={preventNegativeValues}
                                      error={
                                        !!errors?.vas_price_details?.[index]
                                          ?.unit_price
                                      }
                                      helperText={
                                        errors?.vas_price_details?.[index]
                                          ?.unit_price?.message
                                      }
                                    />
                                  )}
                                />
                              ) : (
                                formatter.format(item.unit_price)
                              )}
                            </TableCell>
                            <TableCell className="w-120">
                              {!isView ? (
                                <Controller
                                  size="small"
                                  name={`vas_price_details.${index}.quantity`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      onChange={(e) => {
                                        field.onChange(e);
                                        handleChange(index);
                                      }}
                                      label=""
                                      type="number"
                                      size="small"
                                      variant="standard"
                                      sx={highlightedInputStyle}
                                      onKeyDown={preventNegativeValues}
                                      error={
                                        !!errors?.vas_price_details?.[index]
                                          ?.quantity
                                      }
                                      helperText={
                                        errors?.vas_price_details?.[index]?.quantity
                                          ?.message
                                      }
                                    />
                                  )}
                                />
                              ) : (
                                item.quantity
                              )}
                            </TableCell>
                          </>
                        )}
                        {!isView && !hideAmountDetails && (
                          <TableCell className="w-120">
                            <Controller
                              size="small"
                              name={`vas_price_details.${index}.margin`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    handleChange(index);
                                  }}
                                  label=""
                                  type="number"
                                  size="small"
                                  variant="standard"
                                  sx={highlightedInputStyle}
                                  onKeyDown={preventNegativeValues}
                                  error={
                                    !!errors?.vas_price_details?.[index]?.margin
                                  }
                                  helperText={
                                    errors?.vas_price_details?.[index]?.margin
                                      ?.message
                                  }
                                />
                              )}
                            />
                          </TableCell>
                        )}
                        {!hideAmountDetails && (
                          <TableCell className="w-120">
                            {!isView ? (
                              <Controller
                                size="small"
                                name={`vas_price_details.${index}.discount`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      handleChange(index);
                                    }}
                                    label=""
                                    type="number"
                                    size="small"
                                    variant="standard"
                                    sx={highlightedInputStyle}
                                    onKeyDown={preventNegativeValues}
                                    error={
                                      !!errors?.vas_price_details?.[index]
                                        ?.discount
                                    }
                                    helperText={
                                      errors?.vas_price_details?.[index]
                                        ?.discount?.message
                                    }
                                  />
                                )}
                              />
                            ) : (
                              formatter.format(
                                // VAS qty is fractional (BigDecimal); do NOT use getDiscountAmtFromPercentage
                                // which truncates via parseInt for order-line Integer qty.
                                item.discount > 0 && item.unit_price > 0 && item.quantity > 0
                                  ? getDiscountAmtExact(
                                      item.discount,
                                      item.unit_price,
                                      item.quantity
                                    )
                                  : 0
                              )
                            )}
                          </TableCell>
                        )}
                        {!hideAmountDetails && (
                          <TableCell className="w-120">
                            {formatter.format(totalPrice || 0)}
                          </TableCell>
                        )}
                        {!isView && (
                          <TableCell className="w-10">
                            <Tooltip title="Delete">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => {
                                  remove(index);
                                }}
                              >
                                <Icon>clear</Icon>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {vasFields.length === 0 && (
            <Box className="flex flex-1 items-center justify-center">
              <div className="my-16 text-red-400 font-medium">{getEmptyMessage()}</div>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default VasPriceList;
